import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormHelperText,
    Checkbox,
    FormControlLabel,
    TextField,
    Divider,
    RadioGroup,
    Radio, Paper, Modal, Snackbar, Alert, LinearProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {axiosInstance, localAxios, localAxiosGet, localAxiosPut } from '../../../../Axios/axios';

// Updated options to match backend enum values exactly - UPPERCASE
const payrollBatchOptions = ['FULL TIME', 'PART TIME'];

const pfSlabOptions = [
    'Restrict Employee and Employer PF to 15,000 slab',
    'Employee and Employer PF on Actual Wage',
    'Employee PF on Actual Wage and Employer to 15,000 Slab'
];

const pfRateOptions = [10, 12, 13];
const employerPfRateOptions = [10, 12, 13];

const esicSlabOptions = [
    'Restrict Contribution to Employee Gross 21,000 slab',
    'No Restriction'
];

const hraOptions = [
    'Metropolitan City -50% of the Basic',
    'Non-Metropolitan City -40% of the Basic'
];

const incomeTaxRegimeOptions = ['Old Regime', 'New Regime'];
const tdsRateOptions = [
    'Tax Slab rate for Salaried Employee',
    'Tax Percentage for Consultant'
];

const exemptionCategoryOptions = [
    'senior-citizen',
    'other-disabled',
    'physically-handicapped',
    'others'
];

const formatBooleanToYesNo = (value) => {
    return value ? 'Yes' : 'No';
};

const formatDisplayText = (value) => {
    if (!value) return '';

    // Special handling for payroll batch to display in title case
    if (value === 'FULL TIME') return 'Full Time';
    if (value === 'PART TIME') return 'Part Time';

    const words = value.toLowerCase().replace(/_/g, ' ').split(' ');
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const SubHeading = ({ children }) => (
    <Typography
        variant="h6"
        sx={{
            mt: 1.5,
            mb: 1,
            pb: 0.5,
            borderBottom: '1px solid #ddd',
            fontWeight: 700
        }}
    >
        {children}
    </Typography>
);

const PayrollDetails = ({
    userId,
    organizationId,
    initialData,
    navigationContext,
    onSaveAndReturn,
    onFormCompletionChange
}) => {
    const [formData, setFormData] = useState({
        // General Setup
        includeInPayroll: 'Yes',
        payrollBatch: '',
        payrollExclusionReason: '',

        // Tax Exemption Checkbox Toggles
        isPtExempt: false,
        isAdditionalTaxExempt: false,

        // PF Fields
        includePf: 'Yes',
        pfSlabType: '',
        employeePfRate: '',
        employerPfRate: '',
        pfExemptionReason: '',

        // ESI Fields
        includeEsi: 'Yes',
        esicSlabType: '',
        esiExemptionReason: '',


        // Gratuity & HRA Fields
        includeGratuity: 'Yes',
        gratuityExemptionReason: '',
        hraType: '',

        // Income Tax Fields
        incomeTaxRegime: '',
        tdsRateType: '',
        tdsPercentage: 0,

        // Exemptions (Checkboxes - Visible only if isAdditionalTaxExempt is true)
        isSeniorCitizen: false,
        isOtherlyDisabled: false,
        isPhysicallyHandicapped: false,
        othersExemption: false,
    });

    const [initialPayrollData, setInitialPayrollData] = useState(null);
    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const navigate = useNavigate()
    const [completion, setCompletion] = useState(0);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    useEffect(() => {
        const fetchEmployeePayrollData = async () => {
            try {
                const response = await axiosInstance(localAxiosGet(`/employeePayrollSettings/getPayrollSettingsById?employeePersonalId=${userId}`));
                if (response.data.status === 'SUCCESS') {
                    setInitialPayrollData(response.data.result);
                    // Don't set isDataLoaded here - it will be set in the mapping useEffect
                } else {
                    // Data not found - set loaded state
                    setIsDataLoaded(true);
                }
            } catch (error) {
                console.error('Error fetching payroll details:', error);
                if (error.response && error.response.status !== 404) {
                    setSnackbar({
                        open: true,
                        message: error.response?.data?.message || 'An unexpected error occurred while fetching Payroll Details.',
                        severity: 'error'
                    });
                }
                setIsDataLoaded(true);
            }
        };

        fetchEmployeePayrollData();
    }, [userId]);

    useEffect(() => {
        if (initialPayrollData) {
            console.log('Loading API data into form:', initialPayrollData);

            setFormData(prevData => ({
                ...prevData,
                // General Setup
                includeInPayroll: initialPayrollData.includeInPayroll ? 'Yes' : 'No',
                payrollBatch: initialPayrollData.payrollBatch || '',
                payrollExclusionReason: initialPayrollData.includeInPayrollReason || '',

                // PF Fields
                includePf: initialPayrollData.includePF ? 'Yes' : 'No',
                pfSlabType: initialPayrollData.pfContributionRate || '',
                employeePfRate: initialPayrollData.employeePfRate ? String(parseFloat(initialPayrollData.employeePfRate)) : '',
                employerPfRate: initialPayrollData.employerPfRate ? String(parseFloat(initialPayrollData.employerPfRate)) : '',
                pfExemptionReason: initialPayrollData.includePFReason || '',

                // ESI Fields
                includeEsi: initialPayrollData.includeESI ? 'Yes' : 'No',
                esicSlabType: initialPayrollData.ESIC_ContributionBasis || '',
                esiExemptionReason: initialPayrollData.includeESIReason || '',

                // Gratuity & HRA Fields
                includeGratuity: initialPayrollData.includeGratuity ? 'Yes' : 'No',
                gratuityExemptionReason: initialPayrollData.includeGratuityReason || '',
                hraType: initialPayrollData.HRACalculationBasis || '',

                // Income Tax Fields
                incomeTaxRegime: initialPayrollData.taxRegime || '',
                tdsRateType: initialPayrollData.tdsType || '',
                tdsPercentage: initialPayrollData.taxPercentage ? parseFloat(initialPayrollData.taxPercentage) : 0,

                // Tax Exemptions
                isAdditionalTaxExempt: initialPayrollData.professionTaxExemption || false,
            }));

            setIsDataLoaded(true);
        }
    }, [initialPayrollData]);

    const calculateProgress = React.useCallback(() => {
        if (!isDataLoaded) {
            console.log('Progress: Data not loaded yet');
            setCompletion(0);
            if (typeof onFormCompletionChange === 'function') {
                onFormCompletionChange(0);
            }
            return;
        }

        console.log('Calculating progress with formData:', formData);

        let requiredFields = [];
        requiredFields.push('includeInPayroll');

        if (formData.includeInPayroll === 'No') {
            requiredFields.push('payrollExclusionReason');
        } else {
            requiredFields.push('payrollBatch');
            requiredFields.push('includePf');
            requiredFields.push('includeEsi');
            requiredFields.push('includeGratuity');
            requiredFields.push('hraType');
            requiredFields.push('incomeTaxRegime');
            requiredFields.push('tdsRateType');

            // Conditional PF fields
            if (formData.includePf === 'Yes') {
                requiredFields.push('pfSlabType');
                requiredFields.push('employeePfRate');
                requiredFields.push('employerPfRate');
            } else {
                requiredFields.push('pfExemptionReason');
            }

            // Conditional ESI fields
            if (formData.includeEsi === 'Yes') {
                requiredFields.push('esicSlabType');
            } else {
                requiredFields.push('esiExemptionReason');
            }

            // Conditional Gratuity fields
            if (formData.includeGratuity === 'No') {
                requiredFields.push('gratuityExemptionReason');
            }

            // Conditional TDS Percentage field
            if (formData.tdsRateType === 'Tax Percentage for Consultant') {
                requiredFields.push('tdsPercentage');
            }
        }

        console.log('Required fields:', requiredFields);

        const filledCount = requiredFields.filter(field => {
            const value = formData[field];
            console.log(`Field ${field}:`, value, 'Type:', typeof value);

            if (field === 'tdsPercentage') {
                const isValid = value !== null && value !== '' && Number(value) > 0;
                console.log(`TDS Percentage validation: ${isValid}`);
                return isValid;
            }

            // Fix: Convert to string properly to handle all types
            const stringValue = String(value || '');
            const isValid = value !== null && value !== undefined && stringValue.trim() !== '';
            console.log(`Field ${field} validation: ${isValid}`);
            return isValid;
        }).length;

        const totalFields = requiredFields.length;
        const percentage = totalFields > 0 ? Math.round((filledCount / totalFields) * 100) : 0;

        console.log(`Progress: ${filledCount}/${totalFields} = ${percentage}%`);

        setCompletion(percentage);

        if (typeof onFormCompletionChange === 'function') {
            onFormCompletionChange(percentage);
        }
    }, [formData, onFormCompletionChange, isDataLoaded]);

    useEffect(() => {
        calculateProgress();
    }, [calculateProgress]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        console.log(`Input change - ${name}:`, value);

        setFormData(prevData => ({
            ...prevData,
            [name]: name === 'tdsPercentage' ? Number(value) : value
        }));
    };

    const handleRadioChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: checked
        }));
    };

    const getFieldLabel = (fieldName) => {
        const fieldLabels = {
            payrollBatch: 'Payroll Batch',
            payrollExclusionReason: 'Payroll Exclusion Reason',
            pfSlabType: 'PF Contribution Rate/Slab',
            employeePfRate: 'Employee PF Rate',
            employerPfRate: 'Employer PF Rate',
            pfExemptionReason: 'PF Exemption Reason',
            esicSlabType: 'ESIC Contribution Basis',
            esiExemptionReason: 'ESI Exemption Reason',
            gratuityExemptionReason: 'Gratuity Exemption Reason',
            hraType: 'HRA Calculation Basis',
            incomeTaxRegime: 'Income Tax Regime',
            tdsRateType: 'TDS Rate Type',
            tdsPercentage: 'TDS Percentage'
        };

        return fieldLabels[fieldName] || fieldName;
    };

    const validateForm = (data) => {
        console.log('=== VALIDATION DEBUG ===');
        console.log('Current formData:', data);
        console.log('includeGratuity:', data.includeGratuity);
        console.log('gratuityExemptionReason:', data.gratuityExemptionReason);
        console.log('gratuityExemptionReason length:', data.gratuityExemptionReason?.length);
        console.log('gratuityExemptionReason trimmed:', data.gratuityExemptionReason?.trim());
        console.log('gratuityExemptionReason trimmed length:', data.gratuityExemptionReason?.trim().length);

        const errors = {};

        // Payroll Exclusion Validation
        if (data.includeInPayroll === 'No') {
            if (!data.payrollExclusionReason?.trim()) {
                errors.payrollExclusionReason = 'Please provide a reason for payroll exclusion';
            }
        } else {
            // General Setup Validations
            if (!data.payrollBatch) {
                errors.payrollBatch = 'Please select a Payroll Batch';
            }
            if (!data.hraType?.trim()) {
                errors.hraType = 'Please select HRA Calculation Basis';
            }
            if (!data.incomeTaxRegime?.trim()) {
                errors.incomeTaxRegime = 'Please select Income Tax Regime';
            }
            if (!data.tdsRateType?.trim()) {
                errors.tdsRateType = 'Please select TDS Rate Type';
            }

            // PF Validations
            if (data.includePf === 'Yes') {
                if (!data.pfSlabType?.trim()) {
                    errors.pfSlabType = 'Please select PF Contribution Rate/Slab';
                }
                if (!data.employeePfRate || Number(data.employeePfRate) <= 0) {
                    errors.employeePfRate = 'Please specify a valid Employee PF Rate';
                }
                if (!data.employerPfRate || Number(data.employerPfRate) <= 0) {
                    errors.employerPfRate = 'Please specify a valid Employer PF Rate';
                }
            } else {
                if (!data.pfExemptionReason?.trim()) {
                    errors.pfExemptionReason = 'Please provide a reason for PF exemption';
                }
            }

            // ESI Validations
            if (data.includeEsi === 'Yes') {
                if (!data.esicSlabType) {
                    errors.esicSlabType = 'Please select ESIC Contribution Basis';
                }
            } else {
                if (!data.esiExemptionReason?.trim()) {
                    errors.esiExemptionReason = 'Please provide a reason for ESI exemption';
                }
            }

            // Gratuity Validations
            if (data.includeGratuity === 'No' && !data.gratuityExemptionReason?.trim()) {
                console.log('GRATUITY ERROR TRIGGERED - Reason is empty or whitespace');
                errors.gratuityExemptionReason = 'Please provide a reason for Gratuity exemption';
            }

            // TDS Percentage Validation
            if (data.tdsRateType === 'Tax Percentage for Consultant' &&
                (!data.tdsPercentage || Number(data.tdsPercentage) <= 0)) {
                errors.tdsPercentage = 'Please enter a valid TDS Percentage for Consultant';
            }
        }

        console.log('Validation errors:', errors);
        return errors;
    };

    const hasFormDataChanged = () => {
        if (!initialPayrollData) return true;

        // Helper to get exemption category from form data
        const getCurrentExemptionCategory = () => {
            if (formData.isSeniorCitizen) return 'senior-citizen';
            else if (formData.isOtherlyDisabled) return 'other-disabled';
            else if (formData.isPhysicallyHandicapped) return 'physically-handicapped';
            else if (formData.othersExemption) return 'others';
            return null;
        };

        const currentExemptionCategory = getCurrentExemptionCategory();

        // Helper to safely compare rates
        const compareRates = (formRate, initialRate) => {
            const formVal = formRate === '' ? null : String(Math.round(parseFloat(formRate) || 0));
            const initialVal = initialRate === null || initialRate === undefined || initialRate === ''
                ? null
                : String(Math.round(parseFloat(initialRate)));
            return formVal !== initialVal;
        };

        return (
            // General Setup
            (formData.includeInPayroll === 'Yes') !== initialPayrollData.includeInPayroll ||
            formData.payrollBatch !== initialPayrollData.payrollBatch ||

            // PF Fields - use the new compareRates helper
            (formData.includePf === 'Yes') !== initialPayrollData.includePF ||
            formData.pfSlabType !== initialPayrollData.pfContributionRate ||
            compareRates(formData.employeePfRate, initialPayrollData.employeePfRate) ||
            compareRates(formData.employerPfRate, initialPayrollData.employerPfRate) ||
            (formData.includePf === 'No' ? formData.pfExemptionReason : '') !== (initialPayrollData.includePFReason || '') ||

            // ESI Fields
            (formData.includeEsi === 'Yes') !== initialPayrollData.includeESI ||
            formData.esicSlabType !== initialPayrollData.ESIC_ContributionBasis ||
            (formData.includeEsi === 'No' ? formData.esiExemptionReason : '') !== (initialPayrollData.includeESIReason || '') ||

            // Gratuity & HRA Fields
            (formData.includeGratuity === 'Yes') !== initialPayrollData.includeGratuity ||
            formData.hraType !== initialPayrollData.HRACalculationBasis ||
            (formData.includeGratuity === 'No' ? formData.gratuityExemptionReason : '') !== (initialPayrollData.includeGratuityReason || '') ||

            // Income Tax Fields
            formData.incomeTaxRegime !== initialPayrollData.taxRegime ||
            formData.tdsRateType !== initialPayrollData.tdsType ||
            Number(formData.tdsPercentage) !== parseFloat(initialPayrollData.taxPercentage) ||

            // Tax Exemptions
            formData.isAdditionalTaxExempt !== initialPayrollData.professionTaxExemption ||
            currentExemptionCategory !== initialPayrollData.exemptionCategory ||

            // Payroll Exclusion Reason
            (formData.includeInPayroll === 'No' ? formData.payrollExclusionReason : '') !== (initialPayrollData.includeInPayrollReason || '')
        );
    };

    const preparePayload = () => {
        let exemptionCategory = null;
        if (formData.isSeniorCitizen) exemptionCategory = 'senior-citizen';
        else if (formData.isOtherlyDisabled) exemptionCategory = 'other-disabled';
        else if (formData.isPhysicallyHandicapped) exemptionCategory = 'physically-handicapped';
        else if (formData.othersExemption) exemptionCategory = 'others';

        // Helper to convert rate strings to numbers or null
        const convertRate = (rate) => {
            if (rate === '' || rate === null || rate === undefined) return null;
            const num = parseFloat(rate);
            return isNaN(num) ? null : num;
        };

        // Base payload structure - send null values when payroll is excluded
        const payload = {
            employeePersonalId: userId,
            includeInPayroll: formData.includeInPayroll === 'Yes',
            includeInPayrollReason: formData.includeInPayroll === 'No' ? formData.payrollExclusionReason : null,
            payrollBatch: formData.includeInPayroll === 'Yes' ? formData.payrollBatch : null,

            includePF: formData.includeInPayroll === 'Yes' ? (formData.includePf === 'Yes') : null,
            includePFReason: formData.includeInPayroll === 'Yes' && formData.includePf === 'No' ? formData.pfExemptionReason : null,
            pfContributionRate: formData.includeInPayroll === 'Yes' && formData.includePf === 'Yes' ? formData.pfSlabType : null,
            employeePfRate: formData.includeInPayroll === 'Yes' && formData.includePf === 'Yes' ? convertRate(formData.employeePfRate) : null,
            employerPfRate: formData.includeInPayroll === 'Yes' && formData.includePf === 'Yes' ? convertRate(formData.employerPfRate) : null,

            includeESI: formData.includeInPayroll === 'Yes' ? (formData.includeEsi === 'Yes') : null,
            includeESIReason: formData.includeInPayroll === 'Yes' && formData.includeEsi === 'No' ? formData.esiExemptionReason : null,
            ESIC_ContributionBasis: formData.includeInPayroll === 'Yes' && formData.includeEsi === 'Yes' ? formData.esicSlabType : null,

            includeGratuity: formData.includeInPayroll === 'Yes' ? (formData.includeGratuity === 'Yes') : null,
            includeGratuityReason: formData.includeInPayroll === 'Yes' && formData.includeGratuity === 'No' ? formData.gratuityExemptionReason : null,

            HRACalculationBasis: formData.includeInPayroll === 'Yes' ? formData.hraType : null,

            taxRegime: formData.includeInPayroll === 'Yes' ? formData.incomeTaxRegime : null,
            tdsType: formData.includeInPayroll === 'Yes' ? formData.tdsRateType : null,
            taxPercentage: formData.includeInPayroll === 'Yes' && formData.tdsRateType === 'Tax Percentage for Consultant' ? Number(formData.tdsPercentage) : null,

            professionTaxExemption: formData.includeInPayroll === 'Yes' ? formData.isAdditionalTaxExempt : null,
            exemptionCategory: formData.includeInPayroll === 'Yes' && formData.isAdditionalTaxExempt ? exemptionCategory : null
        };

        return payload;
    };

    const handleSaveChanges = async () => {
        console.log('SAVE CLICKED - Current form data:', formData);

        // Validate form directly with current data
        const errors = validateForm(formData);

        // If there are errors, show them in snackbar and return
        if (Object.keys(errors).length > 0) {
            console.log('Validation failed with errors:', errors);
            // Update validation errors state for UI display
            setValidationErrors(errors);

            // Create detailed error message for snackbar
            const errorFields = Object.keys(errors);
            let errorMessage = 'Please fix the following errors:\n\n';

            errorFields.forEach((field, index) => {
                const fieldLabel = getFieldLabel(field);
                errorMessage += `${index + 1}. ${fieldLabel}: ${errors[field]}\n`;
            });

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            return;
        }

        // Check if there are actual changes
        if (initialPayrollData && !hasFormDataChanged()) {
            setSnackbar({
                open: true,
                message: 'No changes detected to update.',
                severity: 'info'
            });

            if (navigationContext?.returnToRehire && onSaveAndReturn) {
                onSaveAndReturn();
            }
            return;
        }

        const payload = preparePayload();
        console.log('Sending payload:', payload);

        try {
            let response;
            let isUpdate = false;

            if (initialPayrollData) {
                response = await axiosInstance(localAxiosPut('/employeePayrollSettings/updatePayrollSettings', payload));
                isUpdate = true;
            } else {
                response = await axiosInstance(localAxios('/employeePayrollSettings/createPayrollSettings', payload));
            }

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    message: response.data.message || `Payroll Details ${isUpdate ? 'Updated' : 'Saved'} Successfully!`,
                    severity: 'success'
                });

                setInitialPayrollData(response.data.result || payload);

                if (navigationContext?.returnToRehire && onSaveAndReturn) {
                    onSaveAndReturn();
                }
            }
        } catch (error) {
            console.error('Error saving payroll details:', error);

            if (error.response && error.response.status === 403) {
                setOpenTokenExpirePopup(true);
            }

            const apiError =  error.response?.data?.message ||error.response?.data?.result || 'An unexpected error occurred';

            setSnackbar({
                open: true,
                message: apiError,
                severity: 'error'
            });
        }
    };

    const handleCloseTokenExpirePopup = () => {
        setOpenTokenExpirePopup(false);
        navigate('/');
    };

    const handleCompensationDetailsClick = () => {
        toast.info("Navigating to Compensation Details...");
    };

    const createToggleSection = (name, label, reasonLabel, conditionalChildren) => {
        // Map the radio field names to their corresponding exemption reason field names
        const exemptionFieldMap = {
            'includePf': 'pfExemptionReason',
            'includeEsi': 'esiExemptionReason',
            'includeGratuity': 'gratuityExemptionReason'
        };

        const exemptionReasonField = exemptionFieldMap[name];

        return (
            <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth sx={{ border: '1px solid #eee', p: 1.5, borderRadius: 1 }}>
                    <Typography variant="subtitle1" component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                        {label}
                    </Typography>
                    <RadioGroup row name={name} value={formData[name]} onChange={handleRadioChange} sx={{ mb: 1 }}>
                        <FormControlLabel value="Yes" control={<Radio size="small" color="primary" />} label="Yes (Include)" />
                        <FormControlLabel value="No" control={<Radio size="small" color="primary" />} label="No (Exempt)" />
                    </RadioGroup>

                    {formData[name] === 'Yes' ? (
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            {conditionalChildren}
                        </Grid>
                    ) : (
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label={reasonLabel}
                            name={exemptionReasonField}
                            value={formData[exemptionReasonField]}
                            onChange={handleInputChange}
                            required
                            size="small"
                            sx={{ mt: 1 }}
                            error={!!validationErrors[exemptionReasonField]}
                            helperText={validationErrors[exemptionReasonField] || "This field is required when exemption is selected"}
                        />
                    )}
                </FormControl>
            </Grid>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <SubHeading>Payroll Information</SubHeading>

                {/* Form Completion Progress Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 2 }}>
                    {formData.includeInPayroll === 'Yes' && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleCompensationDetailsClick}
                            size="small"
                        >
                            Add Compensation
                        </Button>
                    )}
                    <Box sx={{ width: 220, textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={600}>
                            {`Form Completion: ${completion}%`}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={completion}
                            sx={{
                                height: 8,
                                borderRadius: 5,
                                mt: 0.5,
                                backgroundColor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: completion === 100 ? '#2ecc71' : '#1976d2'
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={2}>
                {/* Main Toggle: Include in Payroll */}
                <Grid item xs={12}>
                    <FormControl component="fieldset" fullWidth sx={{ border: '1px solid #1976d2', p: 1.5, borderRadius: 1 }}>
                        <Typography variant="subtitle1" component="legend" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
                            Include Employee in Payroll Cycle (Mandatory)
                        </Typography>
                        <RadioGroup row name="includeInPayroll" value={formData.includeInPayroll} onChange={handleRadioChange}>
                            <FormControlLabel value="Yes" control={<Radio size="small" color="primary" />} label="Yes (Process Payroll)" />
                            <FormControlLabel value="No" control={<Radio size="small" color="primary" />} label="No (Exclude)" />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {/* Conditional Content: Only show if payroll is 'Yes' */}
                {formData.includeInPayroll === 'Yes' ? (
                    <>
                        {/* General Setup */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.payrollBatch}>
                                <InputLabel>Payroll Batch</InputLabel>
                                <Select
                                    label="Payroll Batch"
                                    name="payrollBatch"
                                    value={formData.payrollBatch}
                                    onChange={handleInputChange}
                                >
                                    {payrollBatchOptions.map(option => (
                                        <MenuItem key={option} value={option}>
                                            {formatDisplayText(option)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.payrollBatch && (
                                    <FormHelperText error>{validationErrors.payrollBatch}</FormHelperText>
                                )}
                                <FormHelperText>Full Time, Part Time</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                        {/* PF Details */}
                        {createToggleSection(
                            'includePf',
                            'Include Provident Fund (PF)',
                            'Reason for PF Exemption',
                            [
                                <Grid item xs={12} sm={6} key="pfSlab">
                                    <FormControl fullWidth required size="small" error={!!validationErrors.pfSlabType}>
                                        <InputLabel>PF Contribution Rate/Slab</InputLabel>
                                        <Select label="PF Contribution Rate/Slab" name="pfSlabType" value={formData.pfSlabType} onChange={handleInputChange}>
                                            {pfSlabOptions.map(option => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                        {validationErrors.pfSlabType && (
                                            <FormHelperText error>{validationErrors.pfSlabType}</FormHelperText>
                                        )}
                                        <FormHelperText>Restrict to 15,000 Slab or Actual Wage options</FormHelperText>
                                    </FormControl>
                                </Grid>,
                                <Grid item xs={12} sm={3} key="employeePfRate">
                                    <FormControl fullWidth required size="small" error={!!validationErrors.employeePfRate}>
                                        <InputLabel>Employee PF Rate</InputLabel>
                                        <Select
                                            label="Employee PF Rate"
                                            name="employeePfRate"
                                            value={formData.employeePfRate}
                                            onChange={handleInputChange}
                                        >
                                            {pfRateOptions.map(option => (
                                                <MenuItem key={option} value={String(option)}>{option}%</MenuItem>
                                            ))}
                                        </Select>
                                        {validationErrors.employeePfRate && (
                                            <FormHelperText error>{validationErrors.employeePfRate}</FormHelperText>
                                        )}
                                        <FormHelperText>Employee PF Rate (in %)</FormHelperText>
                                    </FormControl>
                                </Grid>,
                                <Grid item xs={12} sm={3} key="employerPfRate">
                                    <FormControl fullWidth required size="small" error={!!validationErrors.employerPfRate}>
                                        <InputLabel>Employer PF Rate</InputLabel>
                                        <Select label="Employer PF Rate" name="employerPfRate" value={formData.employerPfRate} onChange={handleInputChange}>
                                            {employerPfRateOptions.map(option => (
                                                <MenuItem key={option} value={String(option)}>{option}%</MenuItem>
                                            ))}
                                        </Select>
                                        {validationErrors.employerPfRate && (
                                            <FormHelperText error>{validationErrors.employerPfRate}</FormHelperText>
                                        )}
                                        <FormHelperText>Employer PF Rate (in %)</FormHelperText>
                                    </FormControl>
                                </Grid>,
                            ]
                        )}
                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                        {/* ESI Details */}
                        {createToggleSection(
                            'includeEsi',
                            'Include Employees\' State Insurance (ESI)',
                            'Reason for ESI Exemption',
                            [
                                <Grid item xs={12} sm={6} key="esiSlab">
                                    <FormControl fullWidth required size="small" error={!!validationErrors.esicSlabType}>
                                        <InputLabel>ESIC Contribution Basis</InputLabel>
                                        <Select
                                            label="ESIC Contribution Basis"
                                            name="esicSlabType"
                                            value={formData.esicSlabType}
                                            onChange={handleInputChange}
                                        >
                                            {esicSlabOptions.map(option => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                        {validationErrors.esicSlabType && (
                                            <FormHelperText error>{validationErrors.esicSlabType}</FormHelperText>
                                        )}
                                        <FormHelperText>Restrict contribution to Employee Gross ₹21,000 Slab</FormHelperText>
                                    </FormControl>
                                </Grid>,
                            ]
                        )}
                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                        {/* Gratuity & HRA Details */}
                        <Grid item xs={12}>
                            <SubHeading>Other Benefits</SubHeading>
                        </Grid>

                        {createToggleSection(
                            'includeGratuity',
                            'Include Gratuity',
                            'Reason for Gratuity Exemption',
                            []
                        )}

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.hraType}>
                                <InputLabel>HRA Calculation Basis</InputLabel>
                                <Select label="HRA Calculation Basis" name="hraType" value={formData.hraType} onChange={handleInputChange}>
                                    {hraOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.hraType && (
                                    <FormHelperText error>{validationErrors.hraType}</FormHelperText>
                                )}
                                <FormHelperText>50% for Metropolitan / 40% for Non-Metropolitan</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                        {/* Income Tax Details */}
                        <Grid item xs={12}>
                            <SubHeading>Income Tax and TDS Settings</SubHeading>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.incomeTaxRegime}>
                                <InputLabel>Income Tax Regime</InputLabel>
                                <Select label="Income Tax Regime" name="incomeTaxRegime" value={formData.incomeTaxRegime} onChange={handleInputChange}>
                                    {incomeTaxRegimeOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.incomeTaxRegime && (
                                    <FormHelperText error>{validationErrors.incomeTaxRegime}</FormHelperText>
                                )}
                                <FormHelperText>Old Regime or New Regime</FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required size="small" error={!!validationErrors.tdsRateType}>
                                <InputLabel>TDS Rate Type</InputLabel>
                                <Select label="TDS Rate Type" name="tdsRateType" value={formData.tdsRateType} onChange={handleInputChange}>
                                    {tdsRateOptions.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                                {validationErrors.tdsRateType && (
                                    <FormHelperText error>{validationErrors.tdsRateType}</FormHelperText>
                                )}
                                <FormHelperText>Tax Slab (Salaried) or Percentage (Consultant)</FormHelperText>
                            </FormControl>
                        </Grid>

                        {formData.tdsRateType === 'Tax Percentage for Consultant' && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="TDS/Tax Percentage (for Consultants)"
                                    name="tdsPercentage"
                                    type="number"
                                    value={formData.tdsPercentage}
                                    onChange={handleInputChange}
                                    required
                                    error={!!validationErrors.tdsPercentage}
                                    helperText={validationErrors.tdsPercentage || "Required field for consultant TDS rate"}
                                    size="small"
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                        {/* Tax Exemption Section */}
                        <Grid item xs={12}>
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 700 }}>
                                Tax Exemptions (Optional):
                            </Typography>
                        </Grid>

                        {/* Additional Tax Exemption Toggle */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isAdditionalTaxExempt}
                                        onChange={handleCheckboxChange}
                                        name="isAdditionalTaxExempt"
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label="Profession Tax Exemption (Yes/No)"
                                sx={{ fontWeight: 600 }}
                            />
                        </Grid>

                        {/* Conditional Tax Exemption Details */}
                        {formData.isAdditionalTaxExempt && (
                            <Grid container item xs={12} spacing={1} sx={{ pl: 3, pt: 1 }}>
                                <Grid item xs={12} sm={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.isSeniorCitizen}
                                                onChange={handleCheckboxChange}
                                                name="isSeniorCitizen"
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label="Senior Citizen"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.isOtherlyDisabled}
                                                onChange={handleCheckboxChange}
                                                name="isOtherlyDisabled"
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label="Otherly Disabled"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.isPhysicallyHandicapped}
                                                onChange={handleCheckboxChange}
                                                name="isPhysicallyHandicapped"
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label="Physically Handicapped"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.othersExemption}
                                                onChange={handleCheckboxChange}
                                                name="othersExemption"
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label="Others"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </>
                ) : (
                    // Payroll Excluded: Show Reason Text Box
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Reason for Payroll Exclusion"
                            name="payrollExclusionReason"
                            value={formData.payrollExclusionReason}
                            onChange={handleInputChange}
                            required
                            sx={{ mt: 2 }}
                            error={!!validationErrors.payrollExclusionReason}
                            helperText={validationErrors.payrollExclusionReason || "This field is required when payroll is excluded"}
                        />
                    </Grid>
                )}
            </Grid>

            {/* Save Changes Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                {navigationContext?.returnToRehire ? (
                    <Paper
                        elevation={3}
                        sx={{
                            width: '100%',
                            p: 2,
                            bgcolor: 'info.light',
                            borderLeft: '4px solid',
                            borderColor: 'info.main'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Rehire Verification in Progress
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Completing verification for: <strong>{navigationContext.stepLabel || 'Personal & Contact Details'}</strong>
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={handleSaveChanges}
                                disabled={completion !== 100}
                            >
                                Verify & Return to Rehire
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveChanges}
                        sx={{ py: 1.5, px: 4 }}
                    >
                        Save Payroll Settings
                    </Button>
                )}
            </Box>

            <Modal
                open={openTokenExpirePopup}
                onClose={handleCloseTokenExpirePopup}
                aria-labelledby="token-expire-modal-title"
                aria-describedby="token-expire-modal-description"
            >
                <TokenExpirePopup onClose={handleCloseTokenExpirePopup} />
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PayrollDetails;